import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { WorkRuleDialog } from 'components/dialog/work_rules/WorkRuleDialog';
import { WorkRule } from 'repository/timekeeper/@types/WorkRuleModel';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface ContextMenuProps {
    contextMenu: { mouseX: number; mouseY: number } | null;
    setContextMenu: (value: { mouseX: number; mouseY: number } | null) => void;
    selectedRow: WorkRule[];
    reloading: () => void;
    rows: WorkRule[];
}

interface MenuOption {
    name: string;
    icon: any;
    click: () => void;
    color: string;
    divider: boolean;
    disabled: boolean;
    selected: boolean;
    onlyOne: boolean;
}
export const ContextMenu: React.FC<ContextMenuProps> = ({ contextMenu, setContextMenu, selectedRow, reloading, rows }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [localOpen, setLocalOpen] = useState(false);
    const [localMode, setLocalMode] = useState<'add' | 'edit' | 'delete' | 'info'>('add');
    const basePath = buildSettingsBasePath('work_rules');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'work_rules'), [location.pathname]);
    const routeOpen = modalRoute.action === 'add' || (modalRoute.action === 'update' && modalRoute.id !== null) || (modalRoute.action === 'detail' && modalRoute.id !== null);
    const routeMode: 'add' | 'edit' | 'info' = modalRoute.action === 'add' ? 'add' : modalRoute.action === 'detail' ? 'info' : 'edit';
    const open = routeOpen || localOpen;
    const mode = routeOpen ? routeMode : localMode;
    console.log(`selectedRow`, selectedRow);
    console.log(`rows`, rows);

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForDialog = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeDialog = () => {
        if (routeOpen) {
            navigate(basePath);
            return;
        }
        setLocalOpen(false);
    };

    const options: MenuOption[] = [
        {
            name: t('settings.work_rules.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('work_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
        },
        {
            name: t('settings.work_rules.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('work_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length !== 1 ? true : false,
            selected: false,
            onlyOne: true,
        },
        {
            name: t('settings.work_rules.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setLocalMode('delete');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
        },
        {
            name: t('settings.work_rules.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
        },
    ];

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.map((option, index) => (
                    <div key={index}>
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>

                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />

                            {option.selected ? (
                                <Typography sx={{ pl: 3 }} variant="body2" color={option.color}>
                                    {t('settings.paycodes.table.context_menu.selected')}:{selectedRow.length}
                                </Typography>
                            ) : null}

                            {option.onlyOne ? (
                                <Typography sx={{ pl: 3 }} variant="body2" color="text.secondary">
                                    {t('settings.paycodes.table.context_menu.only_one')}
                                </Typography>
                            ) : null}
                        </MenuItem>
                    </div>
                ))}
            </Menu>

            <WorkRuleDialog open={open} onClose={closeDialog} reloading={reloading} rows={rows} mode={mode} selectedRow={selectedRowsForDialog} />
        </>
    );
};
